import { useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./hooks/useAuth";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { CookieConsent } from "./components/CookieConsent";
import { ScrollToTop } from "./components/ScrollToTop";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ShortcutsProvider, useShortcutsModal } from "./contexts/ShortcutsContext";
import GlobalCommandPalette from "./components/GlobalCommandPalette";
import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import { useNavigate } from "react-router-dom";
import { FeatureFlagProvider } from "./contexts/FeatureFlagContext";
import { ConnectivityProvider } from "./contexts/ConnectivityContext";
import { OfflineBanner } from "./components/OfflineBanner";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import Register from "./pages/Register";
import MeetingRoom from "./pages/MeetingRoom";
import AdminPanel from "./pages/AdminPanel";
import JoinMeeting from "./pages/JoinMeeting";
import ProfileSettings from "./pages/ProfileSettings";
import NotFound from "./pages/NotFound";
import CreateMeeting from "./pages/CreateMeeting";
import MicTest from "./pages/MicTest";
import MeetingPlaceholder from "./pages/MeetingPlaceholder";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";
import Security from "./pages/Security";
import Feedback from "./pages/Feedback";
import Status from "./pages/Status";
import CookiePolicy from "./pages/CookiePolicy";
import Index from "./pages/Index";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import DeveloperApps from "./pages/DeveloperApps";
import PlatformApps from "./pages/PlatformApps";
import EmbedMeeting from "./pages/EmbedMeeting";
import CPDashboard from "./pages/control-panel/CPDashboard";
import CPApps from "./pages/control-panel/CPApps";
import CPMeetings from "./pages/control-panel/CPMeetings";
import CPSettings from "./pages/control-panel/CPSettings";
import { WhatsNewProvider } from "./contexts/WhatsNewContext";

// Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // Data stays fresh for 5 minutes
      gcTime: 1000 * 60 * 30, // Cache kept for 30 minutes
      refetchOnWindowFocus: false, // Prevent unnecessary refetches
      retry: 1, // Retry failed requests once
    },
  },
});

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
      <p className="text-muted-foreground">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  const { user, loading } = useAuth();

  console.log('[AppRoutes] Rendering routes, user:', !!user, 'loading:', loading);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <>
      <OfflineBanner />
      <Routes>
        <Route
          path="/"
          element={user ? <Navigate to="/dashboard" replace /> : <Index />}
        />
      <Route
        path="/dashboard"
        element={user ? <Dashboard /> : <Navigate to="/login" replace />}
      />
      <Route 
        path="/auth" 
        element={user ? <Navigate to="/dashboard" replace /> : <AuthRedirect />} 
      />
      <Route
        path="/login"
        element={user ? <Navigate to="/dashboard" replace /> : <Login />}
      />
      <Route
        path="/register"
        element={user ? <Navigate to="/dashboard" replace /> : <Register />}
      />
      <Route 
        path="/join/:meetingCode?" 
        element={<JoinMeeting />} 
      />
      <Route 
        path="/meeting/:meetingId" 
        element={<MeetingRoom />} 
      />
      <Route 
        path="/admin" 
        element={user ? <AdminPanel /> : <Navigate to="/login" replace />} 
      />
      <Route 
        path="/profile" 
        element={user ? <ProfileSettings /> : <Navigate to="/login" replace />} 
      />
      <Route 
        path="/create-meeting" 
        element={<CreateMeeting />} 
      />
      <Route 
        path="/mic-test" 
        element={<MicTest />} 
      />
      <Route 
        path="/meeting-placeholder" 
        element={<MeetingPlaceholder />} 
      />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/features" element={<Features />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/security" element={<Security />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/status" element={<Status />} />
      <Route path="/cookies" element={<CookiePolicy />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route 
        path="/developer-apps" 
        element={user ? <DeveloperApps /> : <Navigate to="/login" replace />} 
      />
      <Route 
        path="/platform" 
        element={user ? <PlatformApps /> : <Navigate to="/login" replace />} 
      />
      <Route path="/embed/:meetingCode" element={<EmbedMeeting />} />
      <Route
        path="/control-panel"
        element={user ? <CPDashboard /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/control-panel/apps"
        element={user ? <CPApps /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/control-panel/meetings"
        element={user ? <CPMeetings /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/control-panel/settings"
        element={user ? <CPSettings /> : <Navigate to="/login" replace />}
      />
      <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function AuthRedirect() {
  const location = useLocation();
  return <Navigate to={`/login${location.search}${location.hash}`} replace />;
}

function ShortcutManager() {
  const navigate = useNavigate();
  const { openModal: openShortcuts } = useShortcutsModal();
  const [commandOpen, setCommandOpen] = useState(false);

  const handleCreateNote = () => {
    setCommandOpen(false);
    navigate('/dashboard?newNote=1');
  };

  useKeyboardShortcuts({
    onNewNote: handleCreateNote,
    onOpenSearch: () => setCommandOpen(true),
  });

  return (
    <GlobalCommandPalette
      open={commandOpen}
      onOpenChange={setCommandOpen}
      onCreateNote={handleCreateNote}
      onOpenShortcuts={openShortcuts}
      onNavigate={(path) => navigate(path)}
    />
  );
}

const App = () => {
  console.log('[App] Rendering MeetHub...');
  
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <ShortcutsProvider>
          <QueryClientProvider client={queryClient}>
            <FeatureFlagProvider>
              <ConnectivityProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <BrowserRouter>
                    <WhatsNewProvider>
                      <ScrollToTop />
                      <AppRoutes />
                      <CookieConsent />
                      <ShortcutManager />
                    </WhatsNewProvider>
                  </BrowserRouter>
                </TooltipProvider>
              </ConnectivityProvider>
            </FeatureFlagProvider>
          </QueryClientProvider>
        </ShortcutsProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
